import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Gym, HydratedPhoto } from '../types';
import { MapPin, Globe, Star, Heart, Navigation, Loader2, ImageOff, ChevronUp, ChevronDown, X, ChevronLeft, ChevronRight, Share2, SearchX } from 'lucide-react';

import { useAuth } from '../contexts/AuthContext';
import { addFavourite, removeFavourite, isFavourite as checkIsFavourite } from '../services/favouritesService';
import { getGymFromDisplay } from '../services/locationService';
import { useRouteStateSlug } from '../hooks/useRouteStateSlug';
import { STYLE_OPTIONS } from '../lib/filters';
import { getGymProfileMetadata, getCorrectGymUrl } from '../lib/seo';
import { buildGymUrl, toDisplaySuburb } from '../lib/slugs';
import Breadcrumb from '../src/components/ui/breadcrumb';
import { generateGymBreadcrumbs } from '../lib/breadcrumbs';
import { DEFAULT_STATE, LAUNCHED_STATES, stateDisplayToSlug, toAddressedLocationSlug } from '../lib/stateCore.mjs';
import { DEFAULT_STATE_DISPLAY, searchSurfaceForPathname, suburbSlugFromPathParts } from '../lib/stateRoutes';
import { useShareOrCopy } from '../src/lib/useShareOrCopy';
import { SeoHead } from '../components/SeoHead';

import { getGymGalleryUrls, getGymOgImageUrl, getGymPhotoMeta, getGymPhotoSrcSet, PhotoAttribution, buildGymAltText } from '../lib/photos';
import { Skeleton } from '../src/components/ui/skeleton';
import { PageContainer } from '../src/components/ui/page-container';
import { Button } from '../src/components/ui/button';
import { EmptyState } from '../src/components/ui/empty-state';
import { DocumentTitle } from '../components/DocumentTitle';
import { hasDirectoryFor } from '../lib/directoryCoverage';
import { Badge } from '../src/components/ui/badge';
import { CarouselArrowButton } from '../src/components/ui/CarouselArrowButton';
import { useHorizontalScrollArrows } from '../src/hooks/useHorizontalScrollArrows';
import { useFocusTrap } from '../src/hooks/useFocusTrap';
import { useFrozenWhile } from '../hooks/useFrozenWhile';



interface GymProfilePageProps {
    placeId: string;
    initialGym?: Gym | null;
}

const GymProfilePage: React.FC<GymProfilePageProps> = ({
    placeId,
    initialGym,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    // R-02: the state this profile route is scoped to. Canonical profile URLs are
    // /[state]/:suburb/gyms/:slug-gID; the legacy /gym* routes carry no state and
    // resolve to DEFAULT_STATE, which is correct — they are NSW-only permanently.
    const routeStateSlug = useRouteStateSlug();
    // #28: snapshot arrival mode at FIRST mount. A direct/Google load starts with
    // location.key === 'default'; capture it in a ref BEFORE the canonical-URL redirect effect
    // below (which navigate(replace)s a drifted/renamed/moved-gym URL and would mint a fresh
    // key, flipping location.key away from 'default'). handleBack branches on this stable flag.
    const { user, openAuthModal } = useAuth();
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    // UI-03: dialog plumbing for the lightbox — same shared trap the nav drawer
    // uses. restoreFocus (hook default true) returns focus to the opener tile's
    // button on close; Escape closes; body scroll locks while open.
    const lightboxRef = useRef<HTMLDivElement>(null);
    const lightboxCloseRef = useRef<HTMLButtonElement>(null);
    useFocusTrap({
        active: lightboxOpen,
        containerRef: lightboxRef,
        initialFocusRef: lightboxCloseRef,
        lockScroll: true,
        onEscape: () => setLightboxOpen(false),
    });
    // Overlay prev/next arrows for the mobile photo carousel (theme A: shared
    // arrow-carousel pattern, same hook as the blog CategoryPillNav). Only shown
    // when the photos overflow the viewport (i.e. more than one photo fits-to-scroll).
    const gallery = useHorizontalScrollArrows<HTMLDivElement>();
    const [expandedSection, setExpandedSection] = useState<string | null>('hours');

    // Data loading state
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);


    // Favourites state
    const [isFavourite, setIsFavourite] = useState(false);
    const [isFavouriteLoading, setIsFavouriteLoading] = useState(false);


    // Display data state
    // Display-collection docs carry a legacy `styles` field (pre-`allStyles`) that
    // getGymFromDisplay retains via spread; the `displayGym?.styles` fallback below
    // is load-bearing (e.g. Thumpboxing), so the state type reflects it.
    const [displayGym, setDisplayGym] = useState<(Gym & { styles?: string[] }) | null>(null);

    // Only treat initialGym as usable pre-render data when it actually matches the
    // CURRENT route. App.tsx's SSG bridge can hand us a STALE gym — the persistent
    // <script id="__SSG_DATA__"> tag keeps returning the first SSG-loaded gym for
    // every later navigation — and pre-revealing that would flash a DIFFERENT gym's
    // content on this URL. On a mismatch, fall back to the normal skeleton→load→reveal.
    const initialGymMatches = !!initialGym && (initialGym.stableId === placeId || initialGym.id === placeId);

    // Page ready state - waits for images to load before showing content
    // Start ready immediately when a MATCHING initialGym is available (SSG or back-nav with state)
    const [pageReady, setPageReady] = useState(initialGymMatches);

    // Unified reveal state - triggers the final atomic reveal
    // Pre-reveal only when a matching initialGym is provided (SSG hydration / cached nav)
    const [isRevealed, setIsRevealed] = useState(initialGymMatches);

    // Track photos that failed to load (404), keyed by URL for sort-order stability
    const [photoErrors, setPhotoErrors] = useState<Record<string, boolean>>({});

    // Actual naturalWidth/naturalHeight captured after image preload
    const [photoDimensions, setPhotoDimensions] = useState<Record<number, { width: number; height: number }>>({});

    /**
     * ⚠️⚠️ UI-129 (loading spec RULED D4, under A2's settle promise) — THE DIMENSIONS THAT
     * ARE ALLOWED TO MOVE THINGS, frozen at first paint. Read this before using
     * `photoDimensions` directly in anything that affects layout.
     *
     * THE DEFECT THIS EXISTS FOR, and note a comment further down this file asserts the
     * opposite: the preload effect's note says the page reveals "once meta settles so the
     * sort/layout are applied before paint". That is true ONLY for the unseeded path. On a
     * matching SSG/Google arrival `isRevealed` starts TRUE, `<main>` is shown by opacity
     * immediately, and the gallery therefore paints with `photoDimensions` still EMPTY.
     * The `getGymPhotoMeta` round-trip then lands and moves two things under a reader who
     * is already looking: `sortedDisplayPhotos` re-sorts, and `allPortraitLayout` can flip
     * the entire desktop gallery to a three-column strip. A2 forbids exactly that.
     *
     * THE RULE: while the page is not yet revealed, track the real dimensions (the
     * unseeded path still gets its smart-fit sort and portrait layout, applied before
     * anything is visible). The moment it is revealed, FREEZE — later dimensions still
     * arrive and still feed non-moving detail, but they can no longer re-order or relayout.
     *
     * ⚠️ THE ACCEPTED COST, ruled with it stated rather than discovered afterwards: on a
     * Google arrival we learn the shapes only after paint, so an all-portrait gym keeps the
     * ordinary layout and never shows the three-strip one. Pre-baked arrivals are the
     * common case, so that layout will rarely appear. Waiting for the shapes before
     * revealing was NOT available — E1 and the instant-content ruling forbid it.
     */
    const layoutDimensions = useFrozenWhile(photoDimensions, isRevealed);

    // Per-photo Google author attributions (gallery index → attributions).
    // Fetched fresh from getGymPhotoMeta; empty/failed → generic credit fallback.
    const [photoAttributions, setPhotoAttributions] = useState<Record<number, PhotoAttribution[]>>({});

    const gym = useMemo((): Gym | null => {
        if (!placeId && !initialGym) return null;

        // Base model: prioritize new initialGym/placeId over old displayGym during transitions
        const base = (placeId && initialGym) ? initialGym : (displayGym || initialGym);
        if (!base) return null;

        // Photos are now loaded via proxy — no stored photos needed
        const photos: (string | HydratedPhoto)[] = [];

        // Priority for tags:
        // 1. Manually expanded/corrected tags from 'display' collection (this fixes Thumpboxing)
        // 2. Initial gym object tags
        const finalStyles = (displayGym?.allStyles?.length ? displayGym.allStyles : (displayGym?.styles?.length ? displayGym.styles : (initialGym?.allStyles || [])));
        const finalAgeGroups = (displayGym?.ageGroups?.length ? displayGym.ageGroups : (initialGym?.ageGroups || []));
        const finalExpLevels = (displayGym?.experienceLevels?.length ? displayGym.experienceLevels : (initialGym?.experienceLevels || []));

        return {
            id: base.id, // Use the real Google Place ID from the doc
            // Photo proxy requests MUST use the AUTHORITATIVE Google Place ID only.
            // We deliberately do NOT fall back to base.id here: directory search results
            // carry a stableId (or a STALE ChIJ id) in `id`, and using it fires a first
            // burst of getGymPhoto requests that 404, then a second (correct) burst once
            // getGymFromDisplay resolves the real id — the double-burst that tripped the
            // 15/min rate limit (429s). With only the real google_place_id, directory
            // navigations fire a single clean burst after hydration; Favourites/search/SSG
            // already pass google_place_id so they are unaffected.
            google_place_id: displayGym?.google_place_id || base.google_place_id,
            name: displayGym?.name || initialGym?.name || '',
            // ⚰️ UI-144 (operator-ruled 2026-08-28) — `isAutoListed` IS GONE FROM THIS PAGE,
            // from `types.ts` and from the SSG bake. The operator ruled the "Auto-sourced"
            // badge STANDARD on every gym page, so the three `{gym?.isAutoListed && …}`
            // guards became `{gym && …}`; they were the ONLY readers of the flag, which
            // left the whole field dead. ⚠️ NO VISIBLE CHANGE: the badge already rendered
            // on all 2,750 pages because three independent defaults made the flag true.
            // ⚠️⚠️ THE GUARD IS ON `gym`, NOT NOTHING, AND THAT IS NOT COSMETIC. The first
            // build of this change removed the condition ENTIRELY, on my claim that these
            // blocks already sat inside a `{gym && (…)}` region. THEY DO NOT — I had read
            // "nearest match above" as enclosure, which it is not. Measured: with `gym`
            // null the badge went from 0 to 3 nodes in the DOM, hidden behind the loading
            // overlay but present in the accessibility tree, asserting something about a
            // gym that was not there. Enclosure is now verified by paren balance.
            // UI-131 slice 1 (operator-ruled 2026-08-26): the `isAutoListed` gate that used
            // to blank a seeded address is GONE. ⚠️ It never fired on data: `isAutoListed`
            // exists on ZERO of 2,750 display documents (measured read-only over the full
            // corpus, vacuity control 20/20). The build defaults it `?? true` and the search
            // projection hardcodes `true`, so the gate suppressed the address ALWAYS, and only
            // the per-view re-check overrode it. Gating the re-check (Part A) without this
            // would have removed the street address from all 2,750 gym pages — confirmed in
            // the shipped markup (0 visible occurrences; its one hit is inside JSON-LD) and in
            // a browser with the backend unreachable, which is precisely the Part A world.
            // ⭐ Net effect: the address now paints IMMEDIATELY instead of after a round-trip.
            addressLine1: displayGym?.addressLine1 || initialGym?.addressLine1 || '',
            suburb: displayGym?.suburb || initialGym?.suburb || '',
            // Absent state field (pre-R3 display docs) resolves to the default
            // state (R2 rule 5).
            state: displayGym?.state ?? initialGym?.state ?? DEFAULT_STATE_DISPLAY,
            postcode: displayGym?.postcode || initialGym?.postcode || '',
            lat: displayGym?.lat || initialGym?.lat || 0,
            lng: displayGym?.lng || initialGym?.lng || 0,
            stableId: displayGym?.stableId || initialGym?.stableId,
            photos,
            google_rating: displayGym?.google_rating ?? (displayGym as any)?.rating ?? initialGym?.google_rating ?? (initialGym as any)?.rating ?? null,
            google_user_ratings_total: displayGym?.google_user_ratings_total ?? (displayGym as any)?.userRatingCount ?? initialGym?.google_user_ratings_total ?? (initialGym as any)?.userRatingCount ?? null,
            websiteUrl: displayGym?.websiteUrl || initialGym?.websiteUrl,
            phoneNumber: (displayGym as any)?.phoneNumber || initialGym?.phoneNumber,
            openingHours: (displayGym as any)?.openingHours || initialGym?.openingHours || [],
            // Tags
            allStyles: finalStyles,
            ageGroups: finalAgeGroups,
            experienceLevels: finalExpLevels,
            // ⚰️ UI-131 slice 4 — `lastCheckedAt` and `lastHydratedAt` were built here and
            // read by nothing. See the tombstone in `types.ts`.
        };
    }, [placeId, initialGym, displayGym]);

    /**
     * The state THIS gym belongs to, as a URL slug — the single value every URL this
     * page builds is addressed from (share link, back-to-directory, and the stable-URL
     * redirect check).
     *
     * Derived from the gym's own `state`, never defaulted while one is known. The
     * `?? DEFAULT_STATE` arm carries the pre-R3 display doc that has no state field and
     * is byte-identical to the former behaviour — the same narrowing lib/seo.ts
     * getGymJsonLd and lib/ssgSeoAssembly.ts already use. Measured: 1,496 of 1,496
     * first-state and 1,267 of 1,267 second-state display documents carry `state`, so
     * the fallback arm is unreachable for every document that exists today.
     */
    const gymStateSlug = gym?.state ? stateDisplayToSlug(gym.state) : DEFAULT_STATE;

    // Gallery photos via proxy (fresh from Google Places API, CDN-cached 24h)
    const displayPhotos = useMemo((): HydratedPhoto[] => {
        const effectivePlaceId = gym?.google_place_id ?? null;
        if (!effectivePlaceId) return [];

        // Generate 3 proxy photo URLs — Hero image is full size, thumbnails are optimized but high quality
        // `index` is carried so a responsive srcset can be derived per photo
        // (it survives the aspect-sort below via the ...photo spread). `url`
        // stays the identity used for React keys + photoErrors.
        return getGymGalleryUrls(effectivePlaceId, 3).map((url, index) => ({
            url,
            width: index === 0 ? 1200 : 800,
            height: index === 0 ? 800 : 533,
            index,
        }));
    }, [gym?.id, gym?.google_place_id]);

    // Smart-fit photo order: Landscape (>=1.1) > Square (0.9–1.1) > Portrait (<0.9)
    // Uses actual naturalWidth/naturalHeight captured during preload; falls back to
    // original order until dimensions are available.
    const sortedDisplayPhotos = useMemo((): HydratedPhoto[] => {
        // UI-129: `layoutDimensions`, NOT `photoDimensions` — frozen at first paint so a
        // late meta response cannot re-order photos under a reader. See its declaration.
        if (Object.keys(layoutDimensions).length === 0) return displayPhotos;
        return [...displayPhotos]
            .map((photo, i) => ({
                photo,
                width:  layoutDimensions[i]?.width  ?? photo.width,
                height: layoutDimensions[i]?.height ?? photo.height,
            }))
            .sort((a, b) => {
                const scoreOf = (w: number, h: number) => {
                    const r = w / h;
                    return r >= 1.1 ? 2 : r >= 0.9 ? 1 : 0;
                };
                return scoreOf(b.width, b.height) - scoreOf(a.width, a.height);
            })
            .map(({ photo, width, height }) => ({ ...photo, width, height }));
    }, [displayPhotos, layoutDimensions]);

    // Place ID in render scope — needed to build responsive srcsets per surface
    // (each gallery surface uses a different width ladder, so we build at render
    // rather than prebaking one srcset string).
    const effectivePlaceId = useMemo(
        () => gym?.google_place_id ?? null,
        [gym?.google_place_id]
    );

    // Build a responsive srcset for a gallery photo, or undefined when we lack
    // the placeId/index (falls back to the plain src). Purely additive — never
    // touches photo.url (the React key / photoErrors key).
    const srcSetFor = useCallback((photo: HydratedPhoto): string | undefined =>
        (effectivePlaceId && photo.index != null)
            ? getGymPhotoSrcSet(effectivePlaceId, photo.index)
            : undefined,
    [effectivePlaceId]);

    // Detect "Portrait-Only" state — triggers 3-column vertical strip layout
    const allPhotosErrored = useMemo((): boolean =>
        sortedDisplayPhotos.length > 0 && sortedDisplayPhotos.every(p => photoErrors[p.url]),
    [sortedDisplayPhotos, photoErrors]);

    const allPortraitLayout = useMemo((): boolean => {
        // UI-129: `layoutDimensions`, NOT `photoDimensions`. This flag swaps the WHOLE
        // desktop gallery between the standard grid and a three-column strip, so it is
        // the larger of the two movements a late meta response used to cause — every
        // photo repositions at once. Frozen at first paint; see the declaration.
        if (Object.keys(layoutDimensions).length < displayPhotos.length) return false;
        return displayPhotos.every((_, i) => {
            const d = layoutDimensions[i];
            return d ? d.width / d.height < 0.9 : false;
        });
    }, [displayPhotos, layoutDimensions]);

    // Deduped author credits for photos that ACTUALLY rendered (not 404-culled).
    // displayPhotos is in gallery-index order, so its position maps to the meta
    // index; sortedDisplayPhotos reorders for layout, so we map each shown photo
    // back to its original index via its URL. Contributors of failed photos are
    // excluded — we never credit a photo that didn't display.
    const displayedCredits = useMemo((): PhotoAttribution[] => {
        if (Object.keys(photoAttributions).length === 0) return [];
        const urlToIndex = new Map(displayPhotos.map((p, i) => [p.url, i]));
        const seen = new Set<string>();
        const credits: PhotoAttribution[] = [];
        for (const photo of sortedDisplayPhotos) {
            if (photoErrors[photo.url]) continue; // only photos that loaded
            const idx = urlToIndex.get(photo.url);
            if (idx === undefined) continue;
            for (const attr of photoAttributions[idx] || []) {
                if (!attr.displayName) continue;
                const key = `${attr.displayName}|${attr.uri || ''}`;
                if (seen.has(key)) continue;
                seen.add(key);
                credits.push(attr);
            }
        }
        return credits;
    }, [sortedDisplayPhotos, displayPhotos, photoErrors, photoAttributions]);

    const getPhotoStyles = useCallback((photo: HydratedPhoto | undefined) => {
        if (!photo) return {};
        const ratio = photo.width / photo.height;
        // "Smart Focal Points": For portraits in landscape boxes, focus on top
        if (ratio < 0.9) return { objectPosition: 'center 20%' };
        return { objectPosition: 'center' };
    }, []);

    // Reset loading states when navigating to a NEW gym — but SKIP the initial
    // mount. On first mount the state is already at its correct initial values,
    // pre-revealed from useState(!!initialGym) when SSG/cached data is present.
    // Running the reset here would blank an already-complete prerendered page to a
    // skeleton and force a redundant refetch — the completed→skeleton→normal flash
    // seen when opening a profile from a Google result. Only reset on a genuine
    // gym→gym placeId change; the background load below then reconciles quietly
    // (isRevealed stays true, so no skeleton ever shows on that first paint).
    const didMountRef = React.useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            // Skip the reset ONLY when the first mount is already pre-revealed with
            // MATCHING data; otherwise fall through and run the reset so the skeleton
            // shows while the correct gym loads (guards against a stale initialGym).
            if (initialGymMatches) return;
        }
        setDisplayGym(null);
        setPageReady(false);
        setLoadError(false);
        setIsRevealed(false);
        setPhotoErrors({});
        setPhotoDimensions({});
        // UI-129: nothing to reset for the layout freeze — `setIsRevealed(false)` above
        // unfreezes `useFrozenWhile` on its own, which is precisely why the rule lives in
        // a hook keyed on `isRevealed` rather than in a ref this block has to remember.
        setPhotoAttributions({});
        initialPreloadDoneRef.current = null;
    }, [placeId]);

    // Master Reveal Timeout: Ensures the page triggers a reveal even if hydration or images hang
    useEffect(() => {
        let isCurrent = true;
        const masterTimeout = setTimeout(() => {
            if (isCurrent && !isRevealed) {
                console.debug('[GymProfilePage] Master reveal timeout - forcing reveal');

                // If we have ZERO data after 3 seconds (was 5)
                if (!gym && !isLoading) {
                    setLoadError(true);
                }

                setPageReady(true);
                setIsLoading(false);
                setIsRevealed(true);
            }
        }, 1500); // Fail-safe reveal after 1.5 seconds

        return () => {
            isCurrent = false;
            clearTimeout(masterTimeout);
        };
    }, [placeId, isRevealed, !!gym, isLoading]);

    // Atomic Reveal Coordination: ensures a single "reveal" moment for the entire page
    useEffect(() => {
        // We need: gym data, loading finished, and images pre-decoded
        if (!isRevealed && gym && pageReady && !isLoading) {
            // Buffer to ensure state has settled
            const timer = setTimeout(() => {
                setIsRevealed(true);
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [gym, pageReady, isLoading, isRevealed]);

    // Load profile on mount or placeId change
    //
    // UI-131 PART A (operator-ruled 2026-08-26): the re-check runs ONLY when the page
    // arrived with no usable data. Every real entry path — a prerendered arrival, a
    // GymCard click, a back-navigation — hands us a matching gym already, and the baked
    // payload carries EVERY field this page renders (measured against all three seeding
    // sources; the sole exception was `lastHydratedAt`, whose display was removed in the
    // same slice). So on a normal visit this read fetched data the page already had.
    // What survives is the fallback that lets an UNSEEDED url still render — which is
    // also the only occasion `loadError` can now fire, closing UI-123 by construction
    // rather than by building an error state no route could reach.
    useEffect(() => {
        if (initialGymMatches) return;
        let isCurrent = true;
        const load = async () => {
            if (!placeId) return;
            setIsLoading(true);
            setLoadError(false);

            try {
                // Fetch display data - this contains our SATURATED metadata.
                // R-02: pass the route's state — one-arg defaulted to NSW, so a VIC
                // profile read display/nsw/gyms and silently never applied live data.
                let displayData = await getGymFromDisplay(placeId, routeStateSlug);
                // UI-01 review fix (self-heal restored): before state scoping, a
                // wrong-state profile URL still found its gym (everything read NSW)
                // and the canonical-redirect effect below healed the URL. A scoped
                // read alone would dead-end such URLs in the error state instead.
                // On a miss, probe the OTHER launched states — stableIds are
                // corpus-unique, so a cross-state hit is unambiguous, and the
                // redirect effect (keyed off the record's own gym.state) then
                // navigates to the correct URL exactly as before.
                if (!displayData) {
                    for (const probeState of LAUNCHED_STATES) {
                        if (probeState === routeStateSlug) continue;
                        displayData = await getGymFromDisplay(placeId, probeState);
                        if (displayData) break;
                    }
                }
                if (isCurrent && displayData) {
                    setDisplayGym(displayData);
                } else if (!initialGym) {
                    setLoadError(true);
                }
            } catch (error) {
                console.error('[GymProfilePage] Error loading gym:', error);
                if (isCurrent && !initialGym) {
                    setLoadError(true);
                }
            } finally {
                if (isCurrent) {
                    setIsLoading(false);
                }
            }
        };

        load();
        return () => {
            isCurrent = false;
        };
    }, [placeId, routeStateSlug, initialGymMatches]);

    // One getGymPhotoMeta round-trip carries BOTH author attributions and
    // Google's authoritative photo dimensions, so we no longer pre-probe a
    // 200x200 image per gallery photo just to measure aspect ratio (Phase 0
    // item 5 - one fewer paid Places-Photo fetch per gallery image). Dimensions
    // (keyed by photo index) drive the smart-fit sort + portrait layout; the
    // gallery <img> elements load themselves, each with its own onError. We
    // reveal once meta settles so the sort/layout are applied before paint; the
    // master fail-safe above still guards a slow/failed meta.
    const initialPreloadDoneRef = React.useRef<string | null>(null);

    useEffect(() => {
        if (!gym || isLoading) return;

        const isNewGym = initialPreloadDoneRef.current !== gym.id;
        if (!isNewGym && pageReady) return;
        if (isNewGym) setPageReady(false);

        let isCurrent = true;
        const reveal = () => {
            if (!isCurrent) return;
            initialPreloadDoneRef.current = gym.id;
            setPageReady(true);
        };

        if (!effectivePlaceId) {
            reveal();
            return () => { isCurrent = false; };
        }

        getGymPhotoMeta(effectivePlaceId)
            .then(meta => {
                if (!isCurrent) return;
                setPhotoAttributions(meta.attributions);
                if (Object.keys(meta.dimensions).length > 0) setPhotoDimensions(meta.dimensions);
            })
            .catch(() => { if (isCurrent) setPhotoAttributions({}); })
            .finally(reveal);

        // Fail-safe: reveal regardless after 4s.
        const timeout = setTimeout(reveal, 4000);
        return () => { isCurrent = false; clearTimeout(timeout); };
    }, [gym?.id, isLoading, pageReady, effectivePlaceId]);

    // Check if gym is favourited
    useEffect(() => {
        const checkFavourite = async () => {
            if (user && gym?.id) {
                try {
                    const result = await checkIsFavourite(user.uid, gym.id, gym.google_place_id ?? undefined);
                    setIsFavourite(result);
                } catch (error) {
                    console.error('Error checking favourite:', error);
                }
            } else {
                setIsFavourite(false);
            }
        };
        checkFavourite();
    }, [user, gym?.id]);




    // Share-or-copy hook drives the Share button's "Copied!" status flip and
    // the central toast announce. URL/title built inline at click time so the
    // payload always reflects the current gym (canonical URL via buildGymUrl;
    // title format mirrors getGymProfileMetadata's canonical SEO title with
    // the en-dash U+2013 separator).
    const { status: shareStatus, share: shareGym } = useShareOrCopy();
    const handleShare = useCallback(() => {
        if (!gym) return;
        // The shared URL is the gym's own address, so its state must come from the gym —
        // not from the default. Same narrowing as lib/seo.ts getGymJsonLd and
        // lib/ssgSeoAssembly.ts: an absent state (pre-R3 display doc) resolves to the
        // default, which is byte-identical to the former behaviour.
        const url = `https://martialartnetwork.com.au${buildGymUrl(gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode, gymStateSlug)}`;
        const title = `${gym.name} – ${gym.suburb}, ${gym.state || DEFAULT_STATE_DISPLAY} – Martial Arts Network`;
        shareGym({ title, url });
    }, [gym, gymStateSlug, shareGym]);

    // #28: two-mode "Back". Arrived IN-APP (e.g. the DirectoryPage search → this gym) → there is
    // history to return to, so the parent's browser-back (navigate(-1)) lands back on the
    // persistent DirectoryPage with its search results intact. Arrived DIRECTLY (e.g. a Google
    // result) → no in-app history, so browser-back would dead-end / leave the site; instead load
    // the gym's suburb directory (/nsw/[suburb]/gyms auto-loads that suburb's results from the
    // URL path — no triggerFilters, which would only pop the filter modal).
    // 🪦 `handleBack` LIVED HERE — removed 2026-08-16 with the Back button it drove.
    // It branched on `arrivedDirectRef`: cold arrivals were routed to the gym's suburb
    // directory, in-app arrivals popped history via `onBack()`. Full reasoning at the
    // button's tombstone in the markup below.

    // Handle favourite toggle
    const handleFavouriteToggle = useCallback(async () => {
        if (!gym) return;
        if (!user) {
            openAuthModal({
                type: 'favourite',
                gymId: gym.id,
                returnUrl: location.pathname
            });
            return;
        }

        setIsFavouriteLoading(true);
        try {
            if (isFavourite) {
                await removeFavourite(user.uid, gym.id);
                setIsFavourite(false);
            } else {
                await addFavourite(user.uid, gym.stableId || gym.id, 'gym');
                setIsFavourite(true);
            }
        } catch (error) {
            console.error('Error toggling favourite:', error);
        } finally {
            setIsFavouriteLoading(false);
        }
    }, [user, gym, isFavourite, openAuthModal, location.pathname]);


    // Helper logic for rendering tags

    const sanitizeStyles = (styles: string[]): string[] => {
        const canonicalSet = new Set(STYLE_OPTIONS as readonly string[]);
        return styles.filter(s => canonicalSet.has(s));
    };



    const normalizeAgeGroups = (ageGroups: string[]): string[] => {
        return ageGroups.map(ag => {
            if (ag === "Youth (12-17)" || ag === "Teens (12-17)") return "Teens (13-17)";
            if (ag === "Kids (4-11)" || ag === "Children (4-11)") return "Children (5-12)";
            return ag;
        });
    };

    const AGE_GROUP_ORDER = ['Children (5-12)', 'Teens (13-17)', 'Adults (18+)'];
    const sortAgeGroups = (ages: string[]): string[] => {
        return [...ages].sort((a, b) => AGE_GROUP_ORDER.indexOf(a) - AGE_GROUP_ORDER.indexOf(b));
    };

    const EXPERIENCE_ORDER = ['Beginner', 'Intermediate', 'Advanced'];
    const sortExperienceLevels = (levels: string[]): string[] => {
        return [...levels].sort((a, b) => EXPERIENCE_ORDER.indexOf(a) - EXPERIENCE_ORDER.indexOf(b));
    };

    const displayStyles = gym ? sanitizeStyles(gym.allStyles).sort((a, b) => a.localeCompare(b)) : [];
    const displayAgeGroups = gym ? sortAgeGroups(normalizeAgeGroups(gym.ageGroups)) : [];
    const displayExperienceLevels = gym ? sortExperienceLevels(gym.experienceLevels) : [];

    // Google's addressLine1 is typically the FULL formatted address (street, suburb, NSW,
    // postcode, "Australia") — verified consistent across sampled prod gyms. Show it directly
    // (minus the redundant trailing ", Australia"); only append suburb/NSW/postcode when it's
    // somehow NOT already present (safety fallback). Previously we always appended it, which
    // produced the duplicated "…NSW 2015, {suburb} NSW {postcode}".
    const displayAddress = (() => {
        const stateToken = gym?.state || DEFAULT_STATE_DISPLAY;
        const line1 = gym?.addressLine1?.replace(/,\s*Australia\s*$/i, '').trim();
        const tail = `${gym?.suburb ?? ''} ${stateToken} ${gym?.postcode ?? ''}`.trim();
        if (!line1) return tail;
        return new RegExp(` ${stateToken} `).test(line1) ? line1 : `${line1}, ${tail}`;
    })();



    // Deep Link URL
    const googleMapsUrl = placeId
        ? `https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${placeId}`
        : gym ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${gym.name} ${gym.addressLine1} ${gym.suburb} ${gym.state || DEFAULT_STATE_DISPLAY}`)}` : '';

    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const toggleSection = (section: string) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    // Stable URL redirect.
    //
    // The state argument is load-bearing, not decorative: this comparison decides whether
    // to navigate AWAY from the page the visitor asked for. Built without it, a gym in any
    // state other than the default compares its real path against a default-state path,
    // never matches, and redirects every visit to an address that does not exist.
    useEffect(() => {
        if (!gym) return;
        const { correctUrl, needsRedirect } = getCorrectGymUrl(
            location.pathname,
            gym.name,
            gym.id,
            gym.suburb,
            gym.postcode,
            gymStateSlug
        );

        if (needsRedirect) {
            navigate(correctUrl, { replace: true, state: { gym } });
        }
    }, [location.pathname, gym?.name, gym?.id, gym?.suburb, gymStateSlug, navigate]);

    // SEO Meta — use proxy URL for OG image
    const metadata = useMemo(() => {
        if (!gym) return { title: 'Gym Profile', description: '', canonical: '', robots: 'noindex,follow' as const, ogTitle: '', ogDescription: '', ogImage: '', ogUrl: '', ogType: 'website' as const };
        const imageUrl = gym.google_place_id ? getGymOgImageUrl(gym.google_place_id) : undefined;
        // UI-02 (gym half): pass the route's state — omitting the seventh argument
        // defaulted it to DEFAULT_STATE, so on every VIC profile the runtime title
        // (the only metadata field the runtime consumes — useDocumentTitle owns
        // document.title alone) rewrote the tab from the SSG's correct
        // "… Abbotsford VIC 3067 …" to "… NSW …" a moment after load. Same
        // route-derived source as the getGymFromDisplay call above; legacy /gym*
        // routes carry no state segment and resolve to DEFAULT_STATE, which is
        // correct — they are NSW-only permanently.
        return getGymProfileMetadata(gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode, gym.allStyles, imageUrl, routeStateSlug);
    }, [gym?.name, gym?.id, gym?.stableId, gym?.suburb, gym?.postcode, gym?.allStyles, gym?.google_place_id, routeStateSlug]);

    // NOTE: BOTH JSON-LD blocks for this page — the gym's
    // LocalBusiness/SportsActivityLocation (which carries the aggregateRating) AND its
    // BreadcrumbList — are emitted ONCE, by the SSG, into the raw HTML as two separate
    // <script> blocks. See the routeInfo.type === 'gym' branch in vite.config.ts, which
    // ASSEMBLES the tags, and `injectHeadTags` in lib/ssgHead.ts, which WRITES them.
    // Neither is re-emitted here.
    //
    // The breadcrumb used to be runtime-only, which meant it was absent from the
    // prerendered HTML and invisible to any crawler that does not execute JS. It moved
    // to the SSG (P6, 2026-07-25) and the runtime copy was REMOVED in the same change:
    // emitting it here as well would put a SECOND BreadcrumbList in the rendered DOM —
    // the same duplicate-structured-data failure mode that produced the GSC "Review has
    // multiple aggregate ratings" error for the gym block. One emitter per block.

    useEffect(() => {
        if (isRevealed) {
            window.scrollTo(0, 0);
        }
    }, [isRevealed]);

    if (loadError && !initialGym) {
        /**
         * UI-141 — the "Gym not found" screen (operator-ruled 2026-08-28, seven rulings).
         *
         * ⚠️ WHY IT WAS REBUILT RATHER THAN TWEAKED. UI-134 promoted this branch from
         * unreachable to the gym page's ONLY fallback, and it had never been audited. It
         * was the one of the site's FOUR not-found screens (Page, Post, Category, Gym)
         * that did not use the shared `EmptyState`, and consequently the only one with no
         * heading element at all — measured live: `document.querySelectorAll('h1').length`
         * was 0, so heading navigation had nothing to land on.
         *
         * ⭐ THE COPY FIX IS THE ONE THAT MATTERS. The heading said "Gym not found"
         * (this gym does not exist) while the line beneath said "We couldn't load the
         * details for this gym" (a transient failure). For the dominant real case — a
         * dead, mistyped or since-removed link — the second is FALSE, and it invites a
         * refresh that can never succeed. Wording ruled by the operator against the three
         * sibling screens' voice.
         *
         * ⚠️ THE SUBURB LINK IS BUILT FROM THE CANONICAL SLUG, NOT FROM THE URL, and that
         * is load-bearing rather than tidy. `/nsw/bondi-beach-9999/gyms/x-gABC` names a
         * REAL suburb with a WRONG postcode; echoing the url's own segment back would
         * point at `/nsw/bondi-beach-9999/gyms`, which is not a built route — turning one
         * dead end into two. `toAddressedLocationSlug` re-derives `bondi-beach-2026` from
         * the postcode map, so the link is always a route that exists.
         *
         * ⭐ `hasDirectoryFor` (not the hub list) is the coverage oracle: the postcode map
         * is an EXACT 1:1 with the built location routes (re-measured 2026-08-28: nsw
         * 600/600, vic 383/383, nothing on either side alone). `data/hub-locations.json`
         * is FLOORED — 190 + 159 — so using it would hide the button for ~640 suburbs that
         * do have pages. Both already ship in the bundle, so this costs no payload.
         */
        const deadSuburbSlug = suburbSlugFromPathParts(location.pathname.split('/'));
        const deadSuburbDisplay = toDisplaySuburb(deadSuburbSlug);
        const suburbHasPage = hasDirectoryFor(deadSuburbDisplay, routeStateSlug);
        const suburbDirectoryPath = suburbHasPage
            ? `/${routeStateSlug}/${toAddressedLocationSlug(deadSuburbDisplay, routeStateSlug)}/gyms`
            : null;

        return (
            <>
                {/* The tab, the bookmark and the history entry all read "Gym Profile" —
                    an internal label — because <SeoHead> lives in the success branch and
                    the metadata memo's no-gym fallback title leaked through. Named here
                    the way the generic 404 names itself. */}
                <DocumentTitle title="Gym not found – Martial Arts Network" />
                {/* ⚠️⚠️ THE POSITION OF THIS BLOCK WAS RULED BY THE OPERATOR ON 2026-08-29 AND
                    IS NOT INCIDENTAL. It sits near the TOP — header spacer, then the house
                    PageContainer — because the previous version stretched to full height and
                    CENTRED the message, which on a phone put the heading roughly halfway down
                    with the footer pushed off the bottom. The operator ruled it up, matching
                    the site's three other not-found screens.
                    ⚠️ DO NOT re-wrap this in a full-height centring container.
                    ⭐ SUPERSEDED IN PART 2026-08-31: this note used to say nothing tests the
                    position and a regression would be invisible. NOW GUARDED —
                    `e2e/visualGuards.spec.ts` (`npm run test:e2e`) asserts in a real browser
                    at 1280/768/375 that the heading sits in the top region (<350px, >64px),
                    via the real 404 flow; mutation-proved by swapping the spacer class in a
                    served copy (all three widths red, exactly as pre-committed). The ruling
                    stays recorded here because this is where a future editor stands. */}
                <div className="h-header flex-shrink-0" />
                <main id="main-content">
                    <PageContainer>
                        <EmptyState
                            headingLevel={1}
                            icon={SearchX}
                            title="Gym not found"
                            description="The gym you're looking for isn't in our directory. It may have closed, or the link may be out of date."
                            action={
                                <div className="flex flex-wrap items-center justify-center gap-3">
                                    {suburbDirectoryPath && (
                                        <Button asChild>
                                            <Link to={suburbDirectoryPath}>Browse {deadSuburbDisplay} gyms</Link>
                                        </Button>
                                    )}
                                    {/* Secondary only when the suburb link is present — a
                                        lone action keeps the emphasis it has today. */}
                                    <Button variant={suburbDirectoryPath ? 'secondary' : 'primary'} asChild>
                                        <Link to={searchSurfaceForPathname(location.pathname)} state={{ triggerFilters: true }}>Back to directory</Link>
                                    </Button>
                                </div>
                            }
                        />
                    </PageContainer>
                </main>
            </>
        );
    }

    return (
        <div className="min-h-screen bg-surface-canvas flex flex-col auto-scrollbar" style={{ fontFamily: 'Inter, sans-serif' }}>

            <div className="h-header flex-shrink-0" />

            {/* SEO — the tab title only; see components/SeoHead.tsx. Description,
                canonical, robots, OG/Twitter and ALL JSON-LD for this page are
                SSG-injected into the raw HTML.

                A runtime <Helmet> block emitting og:title/description/image/url/type
                stood here and was REMOVED 2026-07-31. It never reached the DOM —
                react-helmet-async was inert under React 19 — and its own comment
                conceded the SSG already emits those tags, which is the copy social
                scrapers read since they do not run JS. Re-emitting them from the
                browser would not help any scraper and would DUPLICATE five og:* tags
                on every gym page. Do not reinstate it. */}
            <SeoHead meta={metadata} />


            {/* M7: the page's ONE <h1>. The desktop (hidden lg:flex) and mobile (lg:hidden)
                layouts below each render the gym name, and BOTH are in the document at all
                times — so every gym page shipped two top-level headings. Rather than pick
                one layout's heading (whichever is display:none at a given width would leave
                the other viewport with none), the name is stated once here, visually hidden,
                and the two layout headings are plain text. Same pattern already used on
                directory pages. No visual change. */}
            {gym?.name && <h1 className="sr-only">{gym.name}</h1>}

            {/* Main Content & Skeleton Container */}
            <div className="relative flex-grow">
                {!isRevealed && (
                    <div className="absolute inset-0 z-20 bg-surface-canvas" role="status" aria-label="Loading gym profile" aria-busy="true">
                        <PageContainer size="content" className="py-6 sm:py-8">
                            {/* Back button skeleton (text-sm "← Back", mb-4) */}
                            <Skeleton className="h-5 w-16 mb-4" />

                            {/* Desktop (lg+) header skeleton — mirrors the loaded two-column header:
                                title + [rating][auto-sourced] pills + address on the left, action
                                controls on the right. Splits at LG to match the loaded header. */}
                            <div className="hidden lg:flex lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
                                <div className="flex-1">
                                    <Skeleton className="h-9 w-72 mb-2" />
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-8 w-20 rounded-full" />
                                        <Skeleton className="h-8 w-28 rounded-full" />
                                        <Skeleton className="h-6 w-56" />
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <Skeleton className="h-11 w-32 rounded-md" />
                                    <Skeleton className="h-11 w-24 rounded-md" />
                                    <Skeleton className="h-11 w-11 rounded-full" />
                                </div>
                            </div>

                            {/* Stacked (below lg) header skeleton — title, then address then pills
                                (mobile), then a button row that at md carries the pills on its line
                                (mirrors the loaded stacked header). Splits at LG to match. */}
                            <div className="flex flex-col lg:hidden mb-6">
                                <Skeleton className="h-8 w-56 mb-2" />
                                <div className="flex flex-col gap-2 mb-4">
                                    <Skeleton className="h-5 w-48" />
                                    <div className="flex items-center gap-2 md:hidden">
                                        <Skeleton className="h-8 w-16 rounded-full" />
                                        <Skeleton className="h-8 w-24 rounded-full" />
                                    </div>
                                </div>
                                <div className="flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3 md:justify-between">
                                    <div className="hidden md:flex items-center gap-2">
                                        <Skeleton className="h-8 w-16 rounded-full" />
                                        <Skeleton className="h-8 w-24 rounded-full" />
                                    </div>
                                    <div className="flex gap-2 md:gap-3 flex-shrink-0">
                                        <Skeleton className="h-11 w-11 md:w-32 rounded-md" />
                                        <Skeleton className="h-11 w-24 rounded-md" />
                                        <Skeleton className="h-11 w-11 rounded-full" />
                                    </div>
                                </div>
                            </div>

                            {/* Desktop Gallery skeleton — 2/3 hero (col-span-2 row-span-2) + two
                                stacked 1/3 thumbnails, mirroring the loaded best-fit grid. */}
                            <div className="hidden md:grid grid-cols-3 gap-3 rounded-2xl overflow-hidden">
                                <Skeleton className="col-span-2 row-span-2 aspect-[2/1] rounded-2xl" />
                                <div className="col-span-1 grid grid-rows-2 gap-3">
                                    <Skeleton className="aspect-[2/1] rounded-2xl" />
                                    <Skeleton className="aspect-[2/1] rounded-2xl" />
                                </div>
                            </div>

                            {/* Mobile Gallery skeleton */}
                            <div className="md:hidden">
                                <Skeleton className="w-full aspect-[2/1] rounded-2xl" />
                            </div>
                        </PageContainer>
                    </div>
                )}

                {/* Main Content - hidden by opacity until isRevealed is triggered */}
                <main id="main-content" className={`${!isRevealed ? 'opacity-0 pointer-events-none' : 'opacity-100 transition-opacity duration-300'}`}>
                {/* ⚠️ `pb-[141px]` IS LOAD-BEARING AND IS NOT A STRAY MAGIC NUMBER — do NOT
                    "tidy" it back to `py-6 sm:py-8`. UI-131 slice 1 removed the Verification
                    micro-footer, which contributed a FIXED 109px (64px margin-top + 24px
                    padding-top + a 1px rule + a 20px label line). Measured live before the
                    change, from the last content block to the top of the footer: 141px at
                    1280 and 768, 161px at 375 (where the date wrapped to its own line). The
                    operator ruled a UNIFORM 141px: desktop and tablet do not move at all, and
                    phones tighten by 20px because that 20px existed only to hold the line of
                    text being deleted. The top padding is deliberately untouched. */}
                <PageContainer size="content" className="pt-6 sm:pt-8 pb-[141px]">

                    {/* UI-08 (F4, operator-ruled 2026-08-15) — the VISIBLE trail.
                        Every gym page was an anchor dead end: measured 0 of 2,750 emitting
                        any link to a locality page, with the trail existing only in JSON-LD
                        that no visitor can click. This renders the SAME spine the SSG's
                        BreadcrumbList already derives from (lib/breadcrumbs.ts), so visible
                        and structured data cannot drift.

                        ⚠️ IT MUST NOT EMIT STRUCTURED DATA, and it does not: <Breadcrumb> is
                        pure visible markup (src/components/ui/breadcrumb.tsx — a <nav> and an
                        <ol>, no <script>). The gym page's BreadcrumbList is emitted ONCE by
                        the SSG and a second one in the hydrated DOM is the documented failure
                        mode this file's note above warns about, and which
                        verify-structured-data.mjs treats as FATAL. Measured on the live page
                        before this change: BreadcrumbList = 1. It must still be 1 after.

                        ⚠️ The conditional is deliberate and it DOES survive prerendering:
                        gym routes carry a matching initialGym at SSG, so `gym` is truthy in
                        the prerender — verified on the live artefact, where the sibling
                        `{gym?.name && <h1 className="sr-only">}` below is present in the
                        baked HTML. A branch that were false at SSG would silently not bake.

                        The Back button below is KEPT: it walks history, which a trail cannot,
                        and removing live behaviour while adding a link is the UI-58 lesson. */}
                    {gym && (
                        <Breadcrumb
                            items={generateGymBreadcrumbs(
                                gym.name,
                                gym.suburb,
                                gymStateSlug,
                                buildGymUrl(gym.name, gym.stableId || gym.id, gym.suburb, gym.postcode, gymStateSlug),
                            )}
                        />
                    )}

                    {/* 🪦 THE "BACK" BUTTON LIVED HERE — REMOVED 2026-08-16 by operator ruling.
                        Its `mb-4` went with it, so the hero bar below now sits directly under the
                        header spacer; no replacement margin is needed and none was added.

                        ⚠️ WHAT WENT WITH IT, RECORDED BECAUSE IT WAS DELIBERATE AND IS NOT OBVIOUS
                        FROM THE MARKUP. This was a TWO-MODE control (#28), not a plain history pop:
                          · arrived IN-APP (a search on the persistent DirectoryPage → this gym) →
                            `onBack()` popped history, landing back on the directory WITH the search
                            results still intact;
                          · arrived COLD (a Google result, a shared link) → there is no in-app history,
                            so instead of dead-ending off-site it loaded the gym's own suburb
                            directory, which auto-runs that suburb's search from the URL path.
                        The second mode is the one with no substitute: a visitor arriving from a search
                        engine now has NO in-page route back to the directory, and the browser's own
                        back button takes them off the site. **The operator was shown this before
                        ruling.** If a "return to results" affordance is ever wanted again, rebuild
                        BOTH modes — a bare `navigate(-1)` is the version that dead-ends. */}

                    {/* HERO HEADER BAR */}
                    <div className={`bg-surface-canvas ${sortedDisplayPhotos && sortedDisplayPhotos.length > 0 && !allPhotosErrored ? 'mb-6' : 'mb-0'}`}>
                        {/* Desktop Layout (lg+) - name left, action buttons right. Below lg the
                            stacked layout takes over so tablet isn't cramped. */}
                        <div className="hidden lg:flex lg:flex-row lg:items-start lg:justify-between gap-4">
                            {/* Left: Name, Rating, Location */}
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <p className="text-2xl md:text-3xl font-semibold text-brand-primary">{gym?.name}</p>
                                </div>


                                {/* Location & Review Info */}
                                <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                                    <div className="flex items-center gap-3">
                                        {/* Rating Badge FIRST (owner: rating before Auto-sourced) */}
                                        {gym?.google_rating && (
                                            <Badge variant="warning" className="px-3 py-1.5 text-sm gap-1">
                                                <Star size={14} fill="currentColor" />
                                                <span className="font-bold">{gym?.google_rating.toFixed(1)}</span>
                                                <span className="font-normal">({gym?.google_user_ratings_total})</span>
                                            </Badge>
                                        )}

                                        {/* Auto-sourced Badge with Tooltip - second */}
                                        {gym && (
                                            <div className="relative group">
                                                <Badge variant="info" className="px-3 py-1.5 text-sm cursor-help">
                                                    Auto-sourced
                                                </Badge>
                                                <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-surface-ink text-white text-xs rounded-lg shadow-elevation-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-64 text-center pointer-events-none">
                                                    This gym is automatically listed from public sources. Information may not be accurate.
                                                    <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-surface-ink rotate-45"></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex items-center text-text-muted">
                                        <MapPin size={16} className="mr-1.5 text-text-muted" />
                                        <span className="text-sm md:text-base">{displayAddress}</span>
                                    </div>
                                </div>


                            </div>

                            {/* Right: CTAs */}
                            <div className="flex items-center gap-3 flex-shrink-0">
                                <Button variant="outline" onClick={() => { if (googleMapsUrl.startsWith('https://')) window.open(googleMapsUrl, '_blank', 'noreferrer'); }}><Navigation size={16} className="mr-0.5" />Directions</Button>
                                {gym?.websiteUrl && /^https?:\/\//i.test(gym.websiteUrl) && (
                                    <Button variant="outline" onClick={() => window.open(gym.websiteUrl, '_blank', 'noreferrer')}><Globe size={16} className="mr-0.5" />Website</Button>
                                )}
                                <Button variant="primary" onClick={handleShare}><Share2 size={16} className="mr-0.5" />{shareStatus === 'copied' ? 'Copied!' : 'Share'}</Button>
                                {/* Favourite Button - Right-aligned, Circular. UI-13: shared Button
                                    (secondary carries the same surface/border/disabled treatment);
                                    className restores the circular 44px geometry and keeps hover
                                    inert (the heart's group-hover/fav fill is the hover affordance). */}
                                <Button
                                    variant="secondary"
                                    onClick={handleFavouriteToggle}
                                    disabled={isFavouriteLoading}
                                    aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                                    className="w-[44px] h-[44px] p-0 rounded-full hover:bg-surface-base transition-all duration-200 group/fav"
                                >
                                    {isFavouriteLoading ? <Loader2 size={30} className="animate-spin text-white" /> : <Heart size={30} strokeWidth={2} className={`stroke-white ${isFavourite ? 'fill-state-danger group-hover/fav:fill-state-danger-subtle' : 'fill-text-muted group-hover/fav:fill-state-danger'}`} />}
                                </Button>

                            </div>


                        </div>

                        {/* Stacked Layout (mobile + tablet, below lg): name, address, then the
                            status/rating pills (owner: address directly under the title, pills under
                            the address), then the action buttons on their own full-width row below. */}
                        <div className="flex flex-col lg:hidden">
                            {/* Gym Name */}
                            <p className="text-2xl md:text-3xl font-semibold text-brand-primary mb-2">{gym?.name}</p>

                            {/* Location & Review Info - Mobile (address first, then pills) */}
                            <div className="flex flex-col gap-2 mb-4">
                                {/* Address - Mobile */}
                                <div className="flex items-center text-text-muted">
                                    <MapPin size={16} className="mr-1.5 text-text-muted flex-shrink-0" />
                                    <span className="text-sm">{displayAddress}</span>
                                </div>

                                {/* Pills — MOBILE ONLY (md:hidden). At md+ (tablet) they move onto the
                                    button row below to share one line with the actions. Rating first. */}
                                <div className="flex items-center gap-2 md:hidden">
                                    {/* Rating Badge - Mobile */}
                                    {gym?.google_rating && (
                                        <Badge variant="warning" className="px-3 py-1.5 text-sm gap-1">
                                            <Star size={14} fill="currentColor" />
                                            <span className="font-bold">{gym?.google_rating.toFixed(1)}</span>
                                            <span className="text-xs font-normal">({gym?.google_user_ratings_total})</span>
                                        </Badge>
                                    )}
                                    {/* Auto-sourced Badge - Mobile */}
                                    {gym && (
                                        <div className="relative group">
                                            <Badge variant="info" className="px-3 py-1.5 text-sm cursor-help">
                                                Auto-sourced
                                            </Badge>
                                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-surface-ink text-white text-xs rounded-lg shadow-elevation-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-64 text-center pointer-events-none">
                                                This gym is automatically listed from public sources. Information may not be accurate.
                                                <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-surface-ink rotate-45"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>





                            {/* CTA row — Directions/Website are icon-only on mobile (fit 375px) and gain
                                labels at md; Share is always labeled. At md (tablet) the rating +
                                Auto-sourced pills share this row (pills left, buttons right via
                                md:justify-between); on mobile the pills are the row above instead. */}
                            <div className="flex flex-wrap md:flex-nowrap items-center gap-2 md:gap-3 md:justify-between">
                                {/* Tablet+ ONLY pills (rating first) — sit on the buttons' line at md */}
                                <div className="hidden md:flex items-center gap-2 min-w-0">
                                    {gym?.google_rating && (
                                        <Badge variant="warning" className="px-3 py-1.5 text-sm gap-1">
                                            <Star size={14} fill="currentColor" />
                                            <span className="font-bold">{gym?.google_rating.toFixed(1)}</span>
                                            <span className="text-xs font-normal">({gym?.google_user_ratings_total})</span>
                                        </Badge>
                                    )}
                                    {gym && (
                                        <div className="relative group">
                                            <Badge variant="info" className="px-3 py-1.5 text-sm cursor-help">
                                                Auto-sourced
                                            </Badge>
                                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3 py-2 bg-surface-ink text-white text-xs rounded-lg shadow-elevation-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-64 text-center pointer-events-none">
                                                This gym is automatically listed from public sources. Information may not be accurate.
                                                <div className="absolute left-1/2 -translate-x-1/2 -top-1 w-2 h-2 bg-surface-ink rotate-45"></div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {/* Action buttons — flex-shrink-0 so labels never truncate at md */}
                                <div className="flex gap-2 md:gap-3 flex-shrink-0">
                                    {/* Directions - icon-only on mobile, labeled md+ */}
                                    <Button variant="outline" aria-label="Directions" onClick={() => { if (googleMapsUrl.startsWith('https://')) window.open(googleMapsUrl, '_blank', 'noreferrer'); }}>
                                        <Navigation size={16} className="md:mr-0.5" /><span className="hidden md:inline">Directions</span>
                                    </Button>
                                    {gym?.websiteUrl && /^https?:\/\//i.test(gym.websiteUrl) && (
                                        <Button variant="outline" aria-label="Website" onClick={() => window.open(gym.websiteUrl, '_blank', 'noreferrer')}>
                                            <Globe size={16} className="md:mr-0.5" /><span className="hidden md:inline">Website</span>
                                        </Button>
                                    )}
                                    {/* Share - labeled at all breakpoints (unlike Directions/Website) */}
                                    <Button variant="primary" aria-label="Share" onClick={handleShare}>
                                        <Share2 size={16} className="mr-0.5" /><span>{shareStatus === 'copied' ? 'Copied!' : 'Share'}</span>
                                    </Button>
                                    {/* Favourite - circular heart icon, last, matched to the desktop
                                        layout. UI-13: shared Button (see desktop favourite comment). */}
                                    <Button
                                        variant="secondary"
                                        onClick={handleFavouriteToggle}
                                        disabled={isFavouriteLoading}
                                        aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                                        className="w-[44px] h-[44px] p-0 flex-shrink-0 rounded-full hover:bg-surface-base transition-all duration-200 group/fav"
                                    >
                                        {isFavouriteLoading ? <Loader2 size={30} className="animate-spin text-white" /> : <Heart size={30} strokeWidth={2} className={`stroke-white ${isFavourite ? 'fill-state-danger group-hover/fav:fill-state-danger-subtle' : 'fill-text-muted group-hover/fav:fill-state-danger'}`} />}
                                    </Button>
                                </div>
                            </div>


                        </div>
                    </div>

                    {/* GALLERY SECTION */}
                    {sortedDisplayPhotos && sortedDisplayPhotos.length > 0 && !allPhotosErrored && (
                        <section className="mb-0">
                            {/* Desktop Gallery - Adaptive Grid */}
                            <div className={`hidden md:grid gap-3 rounded-2xl overflow-hidden ${allPortraitLayout ? 'grid-cols-3 h-[400px]' : 'grid-cols-3'}`}>
                                {allPortraitLayout ? (
                                    // Portrait-Only Fallback: 3 vertical strips
                                    sortedDisplayPhotos.map((photo, index) => (
                                        // UI-03: a real <button>, not a <div onClick> — the tile is now
                                        // keyboard-openable (Enter/Space) and announced as a control.
                                        <button
                                            key={photo.url}
                                            type="button"
                                            aria-label={`View photo ${index + 1} of ${sortedDisplayPhotos.length}`}
                                            className="relative cursor-pointer group h-full bg-surface-canvas border border-border-default rounded-2xl overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset"
                                            onClick={() => openLightbox(index)}
                                        >
                                            {/* UI-126 (spec RULED B5) — `bg-brand-skeleton` on the <img> is the
                                                PENDING tone, and it is applied IDENTICALLY to all FOUR gym-photo
                                                slots in this file (portrait strip, hero, thumbnail slots, mobile
                                                carousel). It paints at once and the photo covers it on load, since
                                                every one of the four is `object-cover` and fills its box; the
                                                MISSING branch renders no <img> at all, so it keeps the parent
                                                button's `bg-surface-canvas` plus its ImageOff icon and the two
                                                states stop being identical.
                                                ⚠️ STATIC BY RULING — do NOT replace this with the shared `Skeleton`
                                                primitive: that primitive is `animate-pulse bg-brand-skeleton`, and
                                                the operator ruled a static tone OVER a pulse. Take its colour, not
                                                its animation.
                                                ⚠️ NOT applied to the lightbox <img> below, deliberately: that one is
                                                `object-contain` on a black backdrop, so a tone would show as a
                                                wrong-sized grey rectangle rather than a placeholder. */}
                                            {!photoErrors[photo.url] ? (
                                                <img
                                                    src={photo.url}
                                                    srcSet={srcSetFor(photo)}
                                                    sizes="(min-width:768px) 22vw, 100vw"
                                                    alt={buildGymAltText(gym ?? {})}
                                                    className="w-full h-full object-cover bg-brand-skeleton transition-transform duration-300 group-hover:scale-105"
                                                    style={getPhotoStyles(photo)}
                                                    loading="lazy"
                                                    decoding="async"
                                                    onError={() => setPhotoErrors(prev => ({ ...prev, [photo.url]: true }))}
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <ImageOff size={48} className="text-text-muted" strokeWidth={1.5} />
                                                </div>
                                            )}
                                            <div className="absolute inset-0" />
                                        </button>
                                    ))
                                ) : (
                                    // Standard Best-Fit Layout (2/3 - 1/3)
                                    <>
                                        {/* UI-03: real <button> (see portrait strip above) */}
                                        <button
                                            type="button"
                                            aria-label={`View photo 1 of ${sortedDisplayPhotos.length}`}
                                            className="col-span-2 row-span-2 relative cursor-pointer group aspect-[2/1] bg-surface-canvas border border-border-default rounded-2xl overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset"
                                            onClick={() => openLightbox(0)}
                                        >
                                            {sortedDisplayPhotos[0] && !photoErrors[sortedDisplayPhotos[0].url] ? (
                                                <img
                                                    src={sortedDisplayPhotos[0].url}
                                                    srcSet={srcSetFor(sortedDisplayPhotos[0])}
                                                    sizes="(min-width:768px) 60vw, 100vw"
                                                    alt={buildGymAltText(gym ?? {})}
                                                    className="w-full h-full object-cover bg-brand-skeleton transition-transform duration-300 group-hover:scale-105"
                                                    style={getPhotoStyles(sortedDisplayPhotos[0])}
                                                    loading="eager"
                                                    decoding="async"
                                                    onError={() => setPhotoErrors(prev => ({ ...prev, [sortedDisplayPhotos[0]!.url]: true }))} /* safe: img only renders when sortedDisplayPhotos[0] exists (guard above) */
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <ImageOff size={48} className="text-text-muted" strokeWidth={1.5} />
                                                </div>
                                            )}
                                            <div className="absolute inset-0" />
                                        </button>

                                        {/* Thumbnail Slots */}
                                        <div className="col-span-1 grid grid-rows-2 gap-3">
                                            {[1, 2].map((index) => {
                                                const photo = sortedDisplayPhotos[index];
                                                return (
                                                    // UI-03: real <button>; disabled when the slot is empty so
                                                    // keyboard users don't land on a control that does nothing.
                                                    <button
                                                        key={photo?.url ?? index}
                                                        type="button"
                                                        disabled={!photo}
                                                        aria-label={photo ? `View photo ${index + 1} of ${sortedDisplayPhotos.length}` : 'No photo in this slot'}
                                                        className="relative group overflow-hidden cursor-pointer aspect-[2/1] bg-surface-canvas border border-border-default rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset disabled:cursor-default"
                                                        onClick={() => photo && openLightbox(index)}
                                                    >
                                                        {photo && !photoErrors[photo.url] ? (
                                                            <>
                                                                <img
                                                                    src={photo.url}
                                                                    srcSet={srcSetFor(photo)}
                                                                    sizes="(min-width:768px) 22vw, 100vw"
                                                                    alt={buildGymAltText(gym ?? {})}
                                                                    className="w-full h-full object-cover bg-brand-skeleton transition-transform duration-300 group-hover:scale-105"
                                                                    style={getPhotoStyles(photo)}
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                    onError={() => setPhotoErrors(prev => ({ ...prev, [photo.url]: true }))}
                                                                />
                                                                <div className="absolute inset-0" />
                                                            </>
                                                        ) : (
                                                            <div className="absolute inset-0 flex items-center justify-center">
                                                                <ImageOff size={24} className="text-text-muted" strokeWidth={1.5} />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Mobile Gallery - Horizontal Swipe with overlay prev/next arrows */}
                            <div className="md:hidden relative">
                            <div
                                ref={gallery.ref}
                                className="-mx-4 sm:-mx-6 px-4 sm:px-6 overflow-x-auto pb-4 hide-scrollbar snap-x snap-mandatory"
                            >
                                <div className="flex gap-3">
                                    {sortedDisplayPhotos.map((photo, index) => (
                                        // UI-03: real <button> (see desktop gallery comments)
                                        <button
                                            key={photo.url}
                                            type="button"
                                            aria-label={`View photo ${index + 1} of ${sortedDisplayPhotos.length}`}
                                            className="flex-shrink-0 w-[85vw] aspect-[2/1] relative cursor-pointer group bg-surface-canvas border border-border-default rounded-2xl overflow-hidden snap-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-inset"
                                            onClick={() => openLightbox(index)}
                                        >
                                            {!photoErrors[photo.url] ? (
                                                <img
                                                    src={photo.url}
                                                    srcSet={srcSetFor(photo)}
                                                    sizes="85vw"
                                                    alt={buildGymAltText(gym ?? {})}
                                                    className="w-full h-full object-cover bg-brand-skeleton transition-transform duration-300 group-hover:scale-105"
                                                    style={getPhotoStyles(photo)}
                                                    loading="lazy"
                                                    onError={() => setPhotoErrors(prev => ({ ...prev, [photo.url]: true }))}
                                                />
                                            ) : (
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <ImageOff size={48} className="text-text-muted" strokeWidth={1.5} />
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-black/0 active:bg-black/10 transition-colors" />
                                        </button>
                                    ))}
                                    {/* Trailing spacer — a REAL flex child (not container padding,
                                        which browsers drop from scrollWidth on overflowing flex, why the
                                        earlier pr-4 fix failed) so the LAST photo gets the same edge gap
                                        as the first (matches the leading px-4 sm:px-6 inset). */}
                                    <div aria-hidden="true" className="flex-shrink-0 w-4 sm:w-6" />
                                </div>
                            </div>
                            {gallery.isOverflowing && (
                                <>
                                    <CarouselArrowButton
                                        direction="prev"
                                        onClick={() => gallery.scrollByFraction(-1, 0.85)}
                                        disabled={!gallery.canScrollPrev}
                                        aria-label="Previous photo"
                                        className="absolute left-2 top-1/2 -mt-2 -translate-y-1/2 z-10 bg-surface-base/85 backdrop-blur-sm shadow-md"
                                    />
                                    <CarouselArrowButton
                                        direction="next"
                                        onClick={() => gallery.scrollByFraction(1, 0.85)}
                                        disabled={!gallery.canScrollNext}
                                        aria-label="Next photo"
                                        className="absolute right-2 top-1/2 -mt-2 -translate-y-1/2 z-10 bg-surface-base/85 backdrop-blur-sm shadow-md"
                                    />
                                </>
                            )}
                            </div>
                            {displayedCredits.length > 0 ? (
                                <p className="text-xs text-text-muted mb-2 text-right">
                                    Photos via Google:{' '}
                                    {displayedCredits.map((credit, i) => (
                                        <React.Fragment key={`${credit.displayName}|${credit.uri || ''}`}>
                                            {i > 0 && ', '}
                                            {/* whitespace-nowrap keeps a multi-word author name (e.g.
                                                "Men in Pink") on one line — it wraps as a whole unit
                                                to the next line rather than splitting mid-name. */}
                                            <span className="whitespace-nowrap">
                                                {credit.uri ? (
                                                    <a
                                                        href={credit.uri}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="underline hover:text-brand-primary"
                                                    >
                                                        {credit.displayName}
                                                    </a>
                                                ) : (
                                                    credit.displayName
                                                )}
                                            </span>
                                        </React.Fragment>
                                    ))}
                                </p>
                            ) : (
                                <p className="text-xs text-text-muted mb-2 text-right">Images sourced via Google</p>
                            )}
                        </section>
                    )}

                    {/* TWO COLUMN LAYOUT: Filters | Contact & Hours */}
                    <div className={`grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-start ${sortedDisplayPhotos && sortedDisplayPhotos.length > 0 && !allPhotosErrored ? 'mt-8' : 'mt-6'}`}>

                        {/* LEFT COLUMN: Filters */}
                        <div className="space-y-8">

                            {/* Martial Arts Styles */}
                            <div className="space-y-3">
                                <h2 className="text-xl font-semibold text-text-primary">Martial Arts Styles</h2>
                                <div className="flex flex-wrap gap-2">
                                    {displayStyles.length > 0 ? (
                                        displayStyles.map(s => (
                                            <span key={s} className="px-3 py-1.5 bg-surface-canvas text-text-primary text-base rounded-full border border-border-default">
                                                {s}
                                            </span>
                                        ))
                                    ) : gym ? (
                                        /* ⚠️ L8/L13 (operator-ruled 2026-08-20) — THE MISSING MIDDLE ARM. Without
                                           this, an empty (or wholly non-canonical) styles array fell straight to the
                                           skeletons below and pulsed FOREVER, because nothing ever resolves it. Gym
                                           pages are prerendered, so that state would BAKE INTO THE SSG HTML and serve
                                           to crawlers until the next build. Measured 0 of 2,750 affected today — this
                                           is the guard, not a fix for a live symptom. Wording MIRRORS the age-groups
                                           and phone arms verbatim rather than inventing copy; copy routes to the
                                           operator. Three arms now match the neighbouring sections: data → empty →
                                           loading. */
                                        <p className="text-text-muted italic">Martial arts styles could not be accurately identified. Please contact the gym for details.</p>
                                    ) : (
                                        <>
                                            <Skeleton className="h-9 w-24 rounded-full" />
                                            <Skeleton className="h-9 w-32 rounded-full" />
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Age Groups */}
                            <div className="space-y-3">
                                <h2 className="text-xl font-semibold text-text-primary">Age Groups</h2>
                                <div className="flex flex-wrap gap-2">
                                    {displayAgeGroups.length > 0 ? (
                                        displayAgeGroups.map(a => (
                                            <span key={a} className="px-3 py-1.5 bg-surface-canvas text-text-primary text-base rounded-full border border-border-default">
                                                {a}
                                            </span>
                                        ))
                                    ) : gym ? (
                                        <p className="text-text-muted italic">Age groups could not be accurately identified. Please contact the gym for details.</p>
                                    ) : (
                                        <>
                                            <Skeleton className="h-9 w-28 rounded-full" />
                                            <Skeleton className="h-9 w-20 rounded-full" />
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Experience Levels */}
                            <div className="space-y-3">
                                <h2 className="text-xl font-semibold text-text-primary">Experience Levels</h2>
                                <div className="flex flex-wrap gap-2">
                                    {displayExperienceLevels.length > 0 ? (
                                        displayExperienceLevels.map(e => (
                                            <span key={e} className="px-3 py-1.5 bg-surface-canvas text-text-primary text-base rounded-full border border-border-default">
                                                {e}
                                            </span>
                                        ))
                                    ) : gym ? (
                                        /* ⚠️ L8/L13 — the same missing middle arm; see the styles section above. */
                                        <p className="text-text-muted italic">Experience levels could not be accurately identified. Please contact the gym for details.</p>
                                    ) : (
                                        <>
                                            <Skeleton className="h-9 w-24 rounded-full" />
                                            <Skeleton className="h-9 w-16 rounded-full" />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN: Phone & Hours */}
                        <div className="space-y-8">

                            {/* Phone */}
                            <div>
                                <h2 className="text-xl font-semibold text-text-primary">Phone</h2>
                                {/* Phone number: underlined at rest (universal tap/click affordance —
                                    visible on touch too, not hover-only); hover deepens to brand-primary. */}
                                {gym?.phoneNumber ? (
                                    <a href={`tel:${gym.phoneNumber}`} className="block mt-3 text-text-primary text-base underline underline-offset-2 hover:text-brand-primary transition-colors">
                                        {gym.phoneNumber}
                                    </a>
                                ) : gym ? (
                                    /* UI-153 (operator-ruled 2026-08-31): the second sentence renders ONLY
                                       when the page actually shows a website button. It used to be
                                       unconditional, and D&P's link corrections created five gyms with
                                       neither phone nor website — pages promising a button that was not
                                       there. ⚠️ The predicate is EXACTLY the website button's own test
                                       (the two `gym?.websiteUrl && /^https?:\/\//` sites above) so the
                                       promise and the button cannot disagree — a malformed URL hides
                                       BOTH, which is the sharp case a bare truthiness check would miss. */
                                    <p className="mt-3 text-text-muted italic">
                                        {gym.websiteUrl && /^https?:\/\//i.test(gym.websiteUrl)
                                            ? "Phone not available. Please visit the gym's website for details."
                                            : 'Phone not available.'}
                                    </p>
                                ) : (
                                    <Skeleton className="h-6 w-48 rounded mt-3" />
                                )}
                            </div>

                            {/* Hours */}
                            <div className="space-y-3 min-h-[300px]">
                                <button
                                    onClick={() => toggleSection('hours')}
                                    className="w-full flex items-center justify-between text-left"
                                >
                                    <h2 className="text-xl font-semibold text-text-primary">Hours</h2>
                                    {expandedSection === 'hours' ? <ChevronUp size={20} className="text-text-muted" /> : <ChevronDown size={20} className="text-text-muted" />}
                                </button>
                                <div className={`space-y-2 text-text-primary text-base ${expandedSection === 'hours' ? 'opacity-100' : 'invisible'}`}>
                                    {gym?.openingHours && gym.openingHours.length > 0 ? (
                                        gym.openingHours.map((dayHours, idx) => (
                                            <div key={idx}>{dayHours}</div>
                                        ))
                                    ) : gym ? (
                                        <p className="text-text-muted italic">Hours not available. Please contact the gym for details.</p>
                                    ) : (
                                        <>
                                            <Skeleton className="h-5 w-full rounded" />
                                            <Skeleton className="h-5 w-full rounded" />
                                            <Skeleton className="h-5 w-3/4 rounded" />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>




                    {/* ⚰️ UI-131 PART B (operator-ruled 2026-08-26) — the "Verification" micro-footer
                        stood here and is GONE: the bordered strip, the label and the
                        "Information last verified" date together.

                        WHY THE WHOLE STRIP, not just the date: the date was fed ONLY by the
                        per-view re-check (`displayGym`), with no baked fallback, so Part A
                        removed it by construction — and the label was rendered
                        UNCONDITIONALLY, which would have left a bordered heading with nothing
                        beside it on all 2,750 gym pages. That empty heading already shipped on
                        every prerendered page, visible until the re-check landed.

                        WHY THE DATE IS NOT SIMPLY MOVED HERE: it was a BATCH stamp, not a
                        per-gym one. Measured over the full corpus: `lastHydratedAt` takes just
                        TWO distinct days across 1,493 NSW gyms (9 Jul ×1,492) and two across
                        1,257 VIC (26 Jul ×1,244). Shown per gym it implied "we checked THIS
                        gym on this date", which was false. The honest statement moved
                        site-wide to `/about` — see `vic/gym-recheck-spec-2026-08-26.md` §3. */}

                </PageContainer>
                </main>
            </div>

            {/* LIGHTBOX — UI-03: a real dialog. role/aria-modal announce it; the
                shared useFocusTrap (wired near the state above) holds Tab inside,
                closes on Escape, locks body scroll, and returns focus to the
                opener tile's button on close. */}
            {
                /* !allPhotosErrored (review fix): the GALLERY unmounts on that flag,
                   and the lightbox's focus-restore target lives in the gallery — the
                   dialog and its opener must share one lifecycle or focus restores
                   into nothing. */
                lightboxOpen && sortedDisplayPhotos && sortedDisplayPhotos.length > 0 && !allPhotosErrored && (
                    <div
                        ref={lightboxRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Photo viewer"
                        className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
                        onClick={() => setLightboxOpen(false)}
                    >
                        <button
                            ref={lightboxCloseRef}
                            type="button"
                            aria-label="Close photo viewer"
                            onClick={() => setLightboxOpen(false)}
                            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white rounded-md"
                        >
                            <X size={32} />
                        </button>
                        {sortedDisplayPhotos[lightboxIndex] && (
                            <img
                                src={sortedDisplayPhotos[lightboxIndex].url}
                                srcSet={srcSetFor(sortedDisplayPhotos[lightboxIndex])}
                                sizes="100vw"
                                alt={`${buildGymAltText(gym ?? {})}, enlarged view`}
                                className="max-w-full max-h-full object-contain"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                        {sortedDisplayPhotos.length > 1 && (
                            <>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxIndex((prev) => (prev > 0 ? prev - 1 : sortedDisplayPhotos.length - 1));
                                    }}
                                    className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors bg-black/20 hover:bg-black/40 p-2 rounded-full"
                                    aria-label="Previous image"
                                >
                                    <ChevronLeft size={48} strokeWidth={1.5} />
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLightboxIndex((prev) => (prev < sortedDisplayPhotos.length - 1 ? prev + 1 : 0));
                                    }}
                                    className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors bg-black/20 hover:bg-black/40 p-2 rounded-full"
                                    aria-label="Next image"
                                >
                                    <ChevronRight size={48} strokeWidth={1.5} />
                                </button>
                            </>
                        )}
                    </div>
                )
            }

        </div >
    );
};

export default GymProfilePage;
